/**
 * Labstep
 *
 * @module services/batch
 * @desc Business logic for processing API calls in batches
 */

import type {
  Batch,
  BatchProcessCreateContainerOwnProps,
} from 'labstep-web/containers/Entity/BatchProcessCreate/types';
import type { EntityNameMap } from 'labstep-web/models';
import { createEntity } from 'labstep-web/state/actions/entity';
import { selectCreateEntityStatus } from 'labstep-web/state/selectors/entity';
import type { LabstepReduxState } from 'labstep-web/state/types';
import type { Dispatch, Store } from 'redux';

export const DEFAULT_MAX_BATCH_SIZE = 10;
const DEFAULT_CONCURRENCY_LIMIT = 1;
const DEFAULT_DELAY_BETWEEN_CALLS = 400;

function sleep(ms: number) {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Wait for a batch request to complete by subscribing to store changes
 */
function waitForBatchCompletion(
  store: Store<LabstepReduxState>,
  entityName: string,
  batchUuid: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const checkStatus = () => {
      const state = store.getState();
      const status = selectCreateEntityStatus(
        state,
        entityName as any,
        batchUuid,
      );

      if (status && !status.isFetching) {
        // Request completed
        unsubscribe();
        if (status.error) {
          reject(new Error(status.error));
        } else {
          resolve();
        }
      }
    };

    // Subscribe to store updates
    const unsubscribe = store.subscribe(checkStatus);

    // Check immediately in case it's already complete
    checkStatus();
  });
}

export const processBatches = async (
  dispatch: Dispatch,
  store: Store<LabstepReduxState>,
  ownProps: BatchProcessCreateContainerOwnProps<keyof EntityNameMap>,
  batches: Batch<keyof EntityNameMap>[],
  options: any,
) => {
  const {
    concurrencyLimit = DEFAULT_CONCURRENCY_LIMIT,
    delayBetweenCalls = DEFAULT_DELAY_BETWEEN_CALLS,
  } = ownProps;

  // Process a single batch with dispatch
  const processBatch = (batch: Batch<keyof EntityNameMap>) => {
    const { batchUuid, items, parentId, parentName } = batch;
    dispatch(
      createEntity(
        ownProps.entityName,
        items as any,
        parentName,
        parentId,
        batchUuid,
        options,
        ownProps.childKeyName,
      ),
    );
    // Return a promise that resolves when the request completes
    return waitForBatchCompletion(
      store,
      ownProps.entityName,
      batchUuid,
    );
  };

  // Process batches with controlled concurrency
  const processWithConcurrency = async () => {
    const executing = new Set<Promise<unknown>>();

    for (const batch of batches) {
      // If we've reached the concurrency limit, wait for one to complete
      if (executing.size >= concurrencyLimit) {
        await Promise.race(executing);
      }

      // Start processing this batch
      const batchPromise = processBatch(batch)
        .then(() => sleep(delayBetweenCalls))
        .finally(() => {
          executing.delete(batchPromise);
        });

      executing.add(batchPromise);
    }

    // Wait for all remaining batches to complete
    await Promise.all(executing);
  };

  await processWithConcurrency();
};
